import React, { useEffect, useState } from 'react';
import { Button, Colors, Input } from '../../../../../ui';
import { ResultInfoForMutation } from '../../../../../ui/ResultInfo';
import { AppNavigator } from '../../../../AppNavigator';
import { useResolve } from '../../../../Container';
import { Copy, Flexer, Title } from '../../../../dashboard/elements/LayoutElements';
import { useSingleSignOnConstants, useSingleSignOnForm, useSingleSignOnMutation } from '../SingleSignOnHooks';
import { CommonFinalStep } from './CommonFinalStep';
import { InputCopyButton } from './InputCopyButton';
import { SetupStep, StepProps } from './SetupStep';
import { SetupStepViewer } from './SetupStepViewer';

const CreateAppStep: React.FC<StepProps> = ({ gotoStep }) => {
    return (
        <SetupStep title="Step 1 — App details">
            <Copy flush>
                <p>From within the Google dashboard:</p>
                <ul>
                    <li>Choose <b>{'Apps > Web and Mobile Apps'}</b> from the left hand navigation</li>
                    <li>Click <b>Add App</b></li>
                    <li>Choose <b>Custom SAML App</b></li>
                </ul>
            </Copy>
            <Copy flush>
                <p>Enter the following details:</p>
                <ul>
                    <li>Name: <b>Ludi</b></li>
                    <li>App Icon Use logo from our <a target="_blank" href="https://ludi.co/media" rel="noreferrer">Media Library</a></li>
                </ul>
            </Copy>
            <Flexer flex="row" justify="end">
                <Button variant="primary" onAction={() => gotoStep(1)}>
                    Next
                </Button>
            </Flexer>
        </SetupStep>
    );
};

const ConfigureServiceProviderStep: React.FC<StepProps> = ({ form, mutation, gotoStep }) => {
    const { draft, draftDirty, patchDraft } = form;

    useEffect(() => {
        mutation.reset();
    }, [draft]);

    const onSave = () => {
        mutation.mutate(draft);
    };

    return (
        <SetupStep title="Step 2 — Google Identity Provider details">
            <Copy flush>
                Here we will configure Ludi with the settings it needs to communicate with the Google IDP service.
                Copy the appopriate values into the inputs below:
            </Copy>
            <Flexer>
                <Input
                    label="SSO URL:"
                    value={draft.entryPoint}
                    error={form.errors.entryPoint}
                    onChange={x => patchDraft({ entryPoint: x })}
                    hint="The endpoint Ludi sends users to authenticate."
                />
            </Flexer>
            <Flexer>
                <Input
                    label="Entity ID:"
                    value={draft.issuer}
                    error={form.errors.issuer}
                    onChange={x => patchDraft({ issuer: x })}
                    hint="This identifies your account to your IDP."
                />
            </Flexer>
            <Flexer>
                <Input
                    label="Certificate:"
                    multiline={{ rows: 3, resize: 'vertical' }}
                    value={draft.certificate}
                    error={form.errors.certificate}
                    onChange={x => patchDraft({ certificate: x })}
                    hint="The certificate your IDP uses to sign assertions."
                />
            </Flexer>
            <Flexer>
                <ResultInfoForMutation mutation={mutation} onSuccess="Settings saved successfully." />
            </Flexer>
            {!mutation.isSuccess && <Flexer flex="row" justify="end">
                <Button disabled={mutation.isLoading || !draftDirty} onAction={onSave}>
                    Save
                </Button>
            </Flexer>}
            {mutation.isSuccess && <>
                <Flexer flex="row" justify="end">
                    <Button variant="primary" onAction={() => gotoStep(2)}>
                        Next
                    </Button>
                </Flexer>
            </>}
        </SetupStep>
    );
};

const ConfigureIdentityProviderStep: React.FC<StepProps> = ({ mutation, gotoStep }) => {
    const { entityId, acsUrl } = useSingleSignOnConstants();

    return (
        <SetupStep title="Step 3 - Service provider details">
            <Copy flush>
                <p>
                    Back on the Google page, click Continue.
                </p>
                <p>
                    On this screen we configure the Google service with the settings it needs to communicate with Ludi.
                </p>
                <p>
                    Input the following settings:
                </p>
            </Copy>
            <Flexer>
                <Input
                    label="ACS URL:"
                    value={acsUrl}
                    after={() => <InputCopyButton value={acsUrl} />}
                    hint="The endpoint your IDP sends the users to when they log in."
                />
            </Flexer>
            <Flexer>
                <Input
                    label="Entity ID:"
                    value={entityId}
                    after={() => <InputCopyButton value={entityId} />}
                    hint="This identifies your account to your IDP."
                />
            </Flexer>
            <Copy flush>
                Leave all other settings as default.
            </Copy>
            <Flexer flex="row" justify="end">
                <Button variant="primary" onAction={() => gotoStep(3)}>
                    Next
                </Button>
            </Flexer>
        </SetupStep>
    );
};

const ConfigureMappingsStep: React.FC<StepProps> = ({ gotoStep }) => {
    return (
        <SetupStep title="Step 4 - Attribute mapping">
            <Copy flush>
                <p>The final step is to configure which profile values Google will send to Ludi. We need to map three of these:</p>
                <ul>
                    <li><b>&ldquo;First name&rdquo;</b> should be mapped to <b>firstName</b></li>
                    <li><b>&ldquo;Last name&rdquo;</b> should be mapped to <b>lastName</b></li>
                    <li><b>&ldquo;Email name&rdquo;</b> should be mapped to <b>email</b></li>
                </ul>
                <figure>
                    <img src="https://docs.ludi.co/~gitbook/image?url=https%3A%2F%2Fcontent.gitbook.com%2Fcontent%2FqEM20VuOReNjoODNwlz5%2Fblobs%2FkfT5yaRdCXUyd2BmbW4f%2Fgoog-sso-3.png&width=768&dpr=4&quality=100&sign=c6573f81&sv=2" />
                </figure>
                <p>Click Continue to complete the setup wizard.</p>
            </Copy>
            <Flexer flex="row" justify="end">
                <Button variant="primary" onAction={() => gotoStep(4)}>
                    Next
                </Button>
            </Flexer>
        </SetupStep>
    );
};

export const SetupGoogleSingleSignOnView = React.memo(() => {

    const nav = useResolve(AppNavigator);
    const [activeStep, setActiveStep] = useState(0);
    const steps = 5;

    const form = useSingleSignOnForm();

    const mutation = useSingleSignOnMutation();

    const gotoStep = (step: number | 'end') => {
        if (step === 'end') {
            nav.go('~/manage/sso');
        }
        else if (step >= 0 && step < steps) {
            setActiveStep(step);
        }
    };

    const stepProps: StepProps = {
        form,
        mutation,
        currentStep: activeStep,
        gotoStep,
    };

    return <Flexer flex="column" gap="2" maxWidth="54rem">
        <Title size="lg" color={Colors.text}>Google Setup</Title>
        <SetupStepViewer steps={steps} activeIndex={activeStep} onStep={gotoStep} />
        {activeStep === 0 && <CreateAppStep {...stepProps} />}
        {activeStep === 1 && <ConfigureServiceProviderStep {...stepProps} />}
        {activeStep === 2 && <ConfigureIdentityProviderStep {...stepProps} />}
        {activeStep === 3 && <ConfigureMappingsStep {...stepProps} />}
        {activeStep === 4 && <CommonFinalStep {...stepProps} />}
    </Flexer>;
});
